import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Package, Route as RouteIcon, Search, Truck, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';
import { useGlobalSearch } from '@/api/hooks/catalog';
import type { SearchHit, SearchHitKind } from '@/api/types';
import { Dialog } from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/State';

const ICONS: Record<SearchHitKind, typeof Package> = {
  order: Package,
  trip: RouteIcon,
  partner: Building2,
  vehicle: Truck,
  driver: User,
};

const KIND_LABEL: Record<SearchHitKind, string> = {
  order: 'Заявка',
  trip: 'Курс',
  partner: 'Контрагент',
  vehicle: 'Бус',
  driver: 'Шофьор',
};

/**
 * Едно поле за всичко, което диспечерът може да държи в главата си: номер на
 * заявка, номер на спедитора, регистрация, име на шофьор. Спедиторът се обажда
 * и казва своя номер — не нашия.
 */
export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term), 180);
    return () => window.clearTimeout(id);
  }, [term]);

  useEffect(() => {
    if (open) {
      setTerm('');
      setDebounced('');
      setCursor(0);
    }
  }, [open]);

  const { data, isFetching } = useGlobalSearch(debounced);
  const hits = useMemo(() => data ?? [], [data]);

  const go = (hit: SearchHit) => {
    onClose();
    switch (hit.kind) {
      case 'order':
        void navigate({ to: '/orders/$orderId', params: { orderId: hit.id } });
        break;
      case 'trip':
        void navigate({ to: '/trips/$tripId', params: { tripId: hit.id } });
        break;
      case 'partner':
        void navigate({ to: '/partners', search: { q: hit.title } });
        break;
      case 'vehicle':
        void navigate({ to: '/vehicles/$vehicleId', params: { vehicleId: hit.id } });
        break;
      case 'driver':
        void navigate({ to: '/drivers/$driverId', params: { driverId: hit.id } });
        break;
    }
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(hits.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === 'Enter') {
      const hit = hits[cursor];
      if (hit) {
        event.preventDefault();
        go(hit);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={t.common.search} width="lg">
      <div className="flex items-center gap-2 rounded-md border border-line-strong bg-surface px-3">
        <Search className="size-4 shrink-0 text-ink-faint" />
        <input
          data-autofocus
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setCursor(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t.common.searchPlaceholder}
          className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-ink-faint"
          aria-label={t.common.search}
        />
        {isFetching ? <Spinner /> : null}
      </div>

      <ul ref={listRef} className="mt-3 max-h-[50svh] overflow-y-auto">
        {hits.map((hit, index) => {
          const Icon = ICONS[hit.kind];
          return (
            <li key={`${hit.kind}-${hit.id}`}>
              <button
                type="button"
                onMouseEnter={() => setCursor(index)}
                onClick={() => go(hit)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left',
                  index === cursor ? 'bg-raised' : 'hover:bg-raised',
                )}
              >
                <Icon className="size-4 shrink-0 text-ink-faint" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">{hit.title}</span>
                  <span className="block truncate text-xs text-ink-dim">{hit.subtitle}</span>
                </span>
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-ink-faint">
                  {KIND_LABEL[hit.kind]}
                </span>
              </button>
            </li>
          );
        })}
        {debounced.length >= 2 && hits.length === 0 && !isFetching ? (
          <li className="px-2 py-6 text-center text-sm text-ink-dim">Няма съвпадения.</li>
        ) : null}
      </ul>
    </Dialog>
  );
}
